/**
 * useBackendHeartbeat - SSE-based backend connectivity monitor
 *
 * Opens an EventSource to /events/station for server-pushed heartbeats.
 * Exposes { connected, lastHeartbeat, syncInFlight } for station UIs.
 *
 * Reconnects with exponential backoff on error (NOT browser-native reconnect,
 * which causes a request storm when offline). Recreates EventSource on auth
 * token refresh to handle JWT expiry.
 *
 * Considers connection "lost" if no heartbeat received in 45 seconds
 * (3× the 15-second server interval).
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:59321';
const STALE_THRESHOLD_MS = 45_000; // 3× heartbeat interval
const STALE_CHECK_INTERVAL_MS = 5_000;
const RECONNECT_BASE_MS = 3_000; // Initial retry delay
const RECONNECT_MAX_MS = 60_000; // Max retry delay (1 minute)

export type HeartbeatState = {
  connected: boolean;
  lastHeartbeat: Date | null;
  syncInFlight: boolean;
};

export function useBackendHeartbeat(): HeartbeatState {
  const [connected, setConnected] = useState(true); // Optimistic initial
  const [lastHeartbeat, setLastHeartbeat] = useState<Date | null>(null);
  const [syncInFlight, setSyncInFlight] = useState(false);

  const eventSourceRef = useRef<EventSource | null>(null);
  const lastHeartbeatRef = useRef<Date | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptRef = useRef(0);
  const lastTokenRef = useRef<string>('');
  const mountedRef = useRef(true);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const createEventSource = useCallback((token: string) => {
    // Close existing connection and cancel pending reconnect
    clearReconnectTimer();
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    if (!token) return;
    lastTokenRef.current = token;

    const es = new EventSource(
      `${BACKEND_URL}/events/station?token=${encodeURIComponent(token)}`
    );

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'heartbeat') {
          const now = new Date();
          lastHeartbeatRef.current = now;
          reconnectAttemptRef.current = 0; // Reset backoff on success
          setLastHeartbeat(now);
          setConnected(true);
          setSyncInFlight(data.syncInFlight ?? false);
        }
      } catch {
        // Ignore malformed messages
      }
    };

    es.onerror = () => {
      // Close immediately to prevent browser-native auto-reconnect storm
      es.close();
      eventSourceRef.current = null;
      setConnected(false);

      // Schedule manual reconnect with exponential backoff
      if (!mountedRef.current) return;
      const attempt = reconnectAttemptRef.current;
      const delay = Math.min(RECONNECT_BASE_MS * Math.pow(2, attempt), RECONNECT_MAX_MS);
      reconnectAttemptRef.current = attempt + 1;

      reconnectTimerRef.current = setTimeout(() => {
        if (!mountedRef.current) return;
        // Use stored token — auth listener will provide a fresh one if it changed
        if (lastTokenRef.current) {
          createEventSource(lastTokenRef.current);
        }
      }, delay);
    };

    eventSourceRef.current = es;
  }, [clearReconnectTimer]);

  useEffect(() => {
    mountedRef.current = true;

    // Get initial session token
    void supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mountedRef.current || !session?.access_token) return;
      createEventSource(session.access_token);
    });

    // Recreate EventSource on token refresh
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mountedRef.current) return;
      if (session?.access_token) {
        reconnectAttemptRef.current = 0; // Reset backoff on fresh token
        createEventSource(session.access_token);
      } else {
        // Signed out
        clearReconnectTimer();
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
        setConnected(false);
      }
    });

    // Staleness check: if no heartbeat in 45s, mark disconnected
    const staleCheckId = setInterval(() => {
      const last = lastHeartbeatRef.current;
      if (last && Date.now() - last.getTime() > STALE_THRESHOLD_MS) {
        setConnected(false);
      }
    }, STALE_CHECK_INTERVAL_MS);

    return () => {
      mountedRef.current = false;
      subscription.unsubscribe();
      clearInterval(staleCheckId);
      clearReconnectTimer();
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [createEventSource, clearReconnectTimer]);

  return { connected, lastHeartbeat, syncInFlight };
}
